import { JSX, useMemo } from "react";

import { Download } from "@mui/icons-material";
import { LinearProgress, Alert, IconButton, Box, Grid, Theme, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useLocationFileQuery, useLocationsDataQuery } from "src/api/locationQueries.api";
import FileFlowMap from "src/components/Files/components/FileFlowMap/FileFlowMap";
import FilesPageBreadcrumbsComponent from "src/components/Files/components/FilesPageBreadcrumbsComponent";
import { downloadHandler } from "src/components/Files/helper/files.helper";
import { type FlowMapWithFrequencyFeatureCollection } from "src/types/FlowMapWithFrequencyFeatureCollection";

const getStyles = (theme: Theme) => ({
  fullwidth: { xs: 12 },
  settingsIcon: { borderRadius: theme.radius.full, borderColor: theme.palette.primary.main, border: 0 },
  breadcrumbsContainer: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 1,
    px: { xs: 1, md: 0 },
  },
});

function isFlowMapWithFrequencyFeatureCollection(obj: FlowMapWithFrequencyFeatureCollection): obj is FlowMapWithFrequencyFeatureCollection {
  return (
    obj &&
    obj.type === "FeatureCollection" &&
    Array.isArray(obj.features) &&
    obj.features.every((f: { type: string }) => f && f.type === "Feature")
  );
}

function FilePreviewPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { locationId, fileId, fileName } = useParams();

  const { data: locations } = useLocationsDataQuery();

  const rootLocation = useMemo(() => locations?.value.find((item) => item.LocalName === "/"), [locations]);
  const selectedLocation = useMemo(() => locations?.value.find((item) => item.Id === locationId), [locationId, locations?.value]);

  const { data, isLoading, error } = useLocationFileQuery(fileId);

  const flowMapData: FlowMapWithFrequencyFeatureCollection | undefined = useMemo(() => {
    if (!data) return undefined;
    if (isFlowMapWithFrequencyFeatureCollection(data as unknown as FlowMapWithFrequencyFeatureCollection)) {
      return data as unknown as FlowMapWithFrequencyFeatureCollection;
    }
    return undefined;
  }, [data]);

  return (
    <Grid container spacing={2}>
      <Grid size={getStyles(theme).fullwidth}>
        <Box sx={getStyles(theme).breadcrumbsContainer}>
          <FilesPageBreadcrumbsComponent selectedFileName={fileName} selectedLocation={selectedLocation} rootLocation={rootLocation} />
          <IconButton
            color='primary'
            sx={getStyles(theme).settingsIcon}
            id='map-settings-button'
            onClick={() => {
              if (fileId && fileName) {
                void downloadHandler(fileId, fileName);
              }
            }}
          >
            <Download />
          </IconButton>
        </Box>
      </Grid>
      <Grid size={getStyles(theme).fullwidth}>
        {isLoading && <LinearProgress color='primary' />}
        {!!error && <Alert severity='error'>{t("Could not load File Content.")}</Alert>}
        {data && flowMapData && <FileFlowMap fileContent={flowMapData} />}
        {data && !flowMapData && (
          <Alert severity='warning'>{t("The file is not in the Diamond App Preview format. Please download the file instead.")}</Alert>
        )}
      </Grid>
    </Grid>
  );
}

export default FilePreviewPage;
